import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBreadcrumbs } from '../context/BreadcrumbContext';
import {
  getQuote, updateQuote, deleteQuote, generateQuotePdf, convertQuoteToOrder,
  getAccounts, getProducts, getUsers, getRepFirms, getContacts, getAccountAddresses,
  createAccountAddress, createContact, getQuoteTemplates, optional
} from '../api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { ArrowLeft, Edit, Trash2, FileText, ShoppingCart, Plus, MapPin, Truck, Package, Info, Eye, Check, X } from 'lucide-react';
import { fmtCurrency } from '../utils/formatCurrency';
import { useToast } from '../context/ToastContext';
import EmptyValue from '../components/EmptyValue';
import TypeaheadSelect from '../components/TypeaheadSelect';
import './AccountDetail/AccountDetail.css';

function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, canDo, isModuleVisible } = useAuth();
  const toast = useToast();
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumbs();
  const [quote, setQuote] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [repFirms, setRepFirms] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);
  const [contactSearchText, setContactSearchText] = useState('');
  const [billToSearchText, setBillToSearchText] = useState('');
  const [shipToSearchText, setShipToSearchText] = useState('');

  // Inline contact creation
  const [showNewContactModal, setShowNewContactModal] = useState(false);
  const [newContactForm, setNewContactForm] = useState({ first_name: '', last_name: '', email: '', phone: '', job_title: '' });

  // Inline address creation
  const [showNewAddressModal, setShowNewAddressModal] = useState(false);
  const [newAddressTarget, setNewAddressTarget] = useState(null);
  const [newAddressForm, setNewAddressForm] = useState({
    address_type: 'Shipping', address_line1: '', address_line2: '',
    city: '', state: '', zip_code: '', country: '', is_headquarters: false, is_default_shipping: false,
  });

  useEffect(() => { loadData(); }, [id]);

  useEffect(() => {
    if (quote && quote.opportunity_name) {
      setBreadcrumbs([
        { label: 'Opportunities', path: '/opportunities' },
        { label: quote.opportunity_name, path: `/opportunities/${quote.opportunity_id}` },
        { label: quote.quote_number },
      ]);
    }
    return () => clearBreadcrumbs();
  }, [quote, setBreadcrumbs, clearBreadcrumbs]);

  const loadData = async () => {
    try {
      // Only the quote itself is required; the reference lists may be
      // module-restricted for this user and must not block the page.
      const [quoteRes, accountsRes, productsRes, usersRes, repFirmsRes, contactsRes] = await Promise.all([
        getQuote(id), optional(getAccounts()), optional(getProducts({ active: 'true' })), optional(getUsers()), optional(getRepFirms()), optional(getContacts()),
      ]);
      setQuote(quoteRes.data);
      setAccounts(accountsRes.data);
      setProducts(productsRes.data);
      setUsers(usersRes.data);
      setRepFirms(repFirmsRes.data);
      setContacts(contactsRes.data);
      if (quoteRes.data.account_id) {
        try { const addr = await getAccountAddresses(quoteRes.data.account_id); setAddresses(addr.data); } catch (e) {}
      }
      try { const t = await getQuoteTemplates(); setTemplates(t.data); } catch (e) {}
    } catch (err) {
      setError('Failed to load quote');
    } finally {
      setLoading(false);
    }
  };

  // Edit mode helpers
  const isLocked = quote?.status === 'Accepted' || quote?.status === 'Rejected';
  const canEdit = canDo('quotes', 'edit') && !isLocked;

  const formatAddressName = (a) => `${a.is_headquarters ? '(HQ) ' : ''}${a.address_type ? `[${a.address_type}] ` : ''}${a.address_line1}, ${a.city}, ${a.state}`;

  const startEditing = () => {
    if (!canEdit) return;
    setFormData({
      status: quote.status || '',
      contact_id: quote.contact_id || '',
      owner_id: quote.owner_id || '',
      rep_firm_id: quote.rep_firm_id || '',
      expiration_date: quote.expiration_date || '',
      estimated_ship_date: quote.estimated_ship_date || '',
      allow_split_shipments: quote.allow_split_shipments || 0,
      payment_terms: quote.payment_terms || '',
      delivery_terms: quote.delivery_terms || '',
      currency: quote.currency || '',
      validity_period: quote.validity_period || '',
      tax_rate: quote.tax_rate || '',
      shipping_cost: quote.shipping_cost || '',
      bill_to_address_id: quote.bill_to_address_id || '',
      ship_to_address_id: quote.ship_to_address_id || '',
      notes: quote.notes || '',
      special_requirements: quote.special_requirements || '',
      terms_and_conditions: quote.terms_and_conditions || '',
    });
    const cont = contacts.find(c => c.id === quote.contact_id);
    setContactSearchText(cont ? `${cont.first_name} ${cont.last_name || ''}`.trim() : '');
    const billAddr = addresses.find(a => a.id === quote.bill_to_address_id);
    setBillToSearchText(billAddr ? formatAddressName(billAddr) : '');
    const shipAddr = addresses.find(a => a.id === quote.ship_to_address_id);
    setShipToSearchText(shipAddr ? formatAddressName(shipAddr) : '');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setFormData({});
  };

  const saveEditing = async () => {
    setSaving(true);
    try {
      await updateQuote(id, { ...quote, ...formData });
      setIsEditing(false);
      setFormData({});
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update quote');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDelete = async () => {
    try { await deleteQuote(id); navigate('/quotes'); } catch (err) { setError(err.response?.data?.error || 'Failed to delete quote'); }
  };

  const handleGeneratePdf = async () => {
    try {
      const response = await generateQuotePdf(id, selectedTemplateId);
      const serverBase = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');
      window.open(`${serverBase}${response.data.pdf_url}`, '_blank');
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Failed to generate PDF'); }
  };

  const handleConvertToOrder = async () => {
    try {
      const response = await convertQuoteToOrder(id);
      toast.success('Quote accepted and order created successfully');
      navigate(`/orders/${response.data.id}`);
    } catch (err) { setError(err.response?.data?.error || 'Failed to convert to order'); }
  };

  // Address creation
  const openNewAddressModal = (target) => {
    setNewAddressTarget(target);
    setNewAddressForm({ address_type: target === 'bill_to' ? 'Billing' : 'Shipping', address_line1: '', address_line2: '', city: '', state: '', zip_code: '', country: '', is_headquarters: false, is_default_shipping: false });
    setShowNewAddressModal(true);
  };

  const handleCreateAddress = async (e) => {
    e.preventDefault();
    if (!quote.account_id) return;
    try {
      const res = await createAccountAddress(quote.account_id, newAddressForm);
      const addressesRes = await getAccountAddresses(quote.account_id);
      setAddresses(addressesRes.data);
      const fieldKey = newAddressTarget === 'bill_to' ? 'bill_to_address_id' : 'ship_to_address_id';
      setFormData(prev => ({ ...prev, [fieldKey]: res.data.id }));
      // Update the typeahead display text
      const newAddr = addressesRes.data.find(a => a.id === res.data.id);
      const displayText = newAddr ? formatAddressName(newAddr) : '';
      if (newAddressTarget === 'bill_to') setBillToSearchText(displayText);
      else setShipToSearchText(displayText);
      setShowNewAddressModal(false);
      toast.success('Address created and selected');
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to create address'); }
  };

  const openNewContactModal = () => {
    setNewContactForm({ first_name: '', last_name: '', email: '', phone: '', job_title: '' });
    setShowNewContactModal(true);
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    if (!quote.account_id) return;
    try {
      const res = await createContact({ ...newContactForm, account_id: quote.account_id });
      const contactsRes = await getContacts();
      setContacts(contactsRes.data);
      updateField('contact_id', res.data.id);
      setContactSearchText(`${newContactForm.first_name} ${newContactForm.last_name}`.trim());
      setShowNewContactModal(false);
      toast.success('Contact created and selected');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create contact');
    }
  };

  const getStatusColor = (s) => {
    switch (s) { case 'Draft': return 'gray'; case 'Sent': return 'primary'; case 'Accepted': return 'success'; case 'Rejected': return 'danger'; default: return 'gray'; }
  };

  if (loading) return <div className="loading-container"><div className="spinner"></div></div>;
  if (!quote) return <div className="alert alert-error">Quote not found</div>;

  const accountContacts = contacts.filter(c => !quote.account_id || c.account_id === parseInt(quote.account_id));

  // Edit mode buttons for card headers
  const editButtons = canEdit ? (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
      {isEditing ? (
        <>
          <button className="btn btn-primary btn-sm" onClick={saveEditing} disabled={saving}>
            <Check size={16} /> {saving ? 'Saving...' : 'Save'}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={cancelEditing} disabled={saving}>
            <X size={16} /> Cancel
          </button>
        </>
      ) : (
        <button className="btn btn-secondary btn-sm" onClick={startEditing}>
          <Edit size={16} /> Edit
        </button>
      )}
    </div>
  ) : null;

  return (
    <div className="quote-detail">
      <div className="page-header">
        <div className="flex items-center gap-4">
          <button className="btn btn-icon" onClick={() => navigate(location.state?.fromOpportunity ? `/opportunities/${location.state.fromOpportunity}` : location.state?.fromAccount ? `/accounts/${location.state.fromAccount}` : '/quotes')} aria-label="Go back"><ArrowLeft size={20} /></button>
          <div>
            <h1 className="page-title">{quote.quote_number}</h1>
            <div className="flex items-center gap-2">
              <span className={`badge badge-${getStatusColor(quote.status)}`}>{quote.status}</span>
              {quote.opportunity_name && (
                <span className="text-secondary">
                  <Link to={`/opportunities/${quote.opportunity_id}`}>{quote.opportunity_name}</Link>
                  {quote.account_name && <> — <Link to={`/accounts/${quote.account_id}`}>{quote.account_name}</Link></>}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="page-actions">
          {quote.opportunity_id && <button className="btn btn-secondary" onClick={() => navigate(`/opportunities/${quote.opportunity_id}`)}><Eye size={18} /> View Opportunity</button>}
          {canDo('quotes', 'edit') && (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {templates.length > 1 && (
                <select className="form-select" style={{ width: 'auto', fontSize: '13px', padding: '6px 10px' }} value={selectedTemplateId || ''} onChange={(e) => setSelectedTemplateId(e.target.value || null)}>
                  <option value="">Default Template</option>
                  {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
              <button className="btn btn-secondary" onClick={handleGeneratePdf}><FileText size={18} /> Generate PDF</button>
            </div>
          )}
          {(quote.status === 'Sent') && isModuleVisible('orders') && canDo('quotes', 'edit') && canDo('orders', 'create') && (
            <button className="btn btn-primary" onClick={handleConvertToOrder}><ShoppingCart size={18} /> Convert to Order</button>
          )}
          {canDo('quotes', 'delete') && <button className="btn btn-danger" onClick={() => setShowDeleteConfirm(true)} aria-label="Delete quote"><Trash2 size={18} /></button>}
        </div>
      </div>

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      {isLocked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)', marginBottom: '16px', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          <Info size={14} />
          {quote.status === 'Accepted' ? 'This quote has been accepted and is locked.' : 'This quote has been rejected.'}
        </div>
      )}

      {/* Quote Details Card */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header">
          <h3 className="card-title">Quote Details</h3>
          {editButtons}
        </div>
        <div className="card-body">
          <div className="detail-row">
            <span className="detail-label">Account</span>
            <span className="detail-value">{isModuleVisible('accounts') ? <Link to={`/accounts/${quote.account_id}`}>{quote.account_name}</Link> : quote.account_name}</span>
          </div>
          {quote.opportunity_id && (
            <div className="detail-row">
              <span className="detail-label">Opportunity</span>
              <span className="detail-value"><Link to={`/opportunities/${quote.opportunity_id}`}>{quote.opportunity_name}</Link></span>
            </div>
          )}
          <div className="detail-row">
            <span className="detail-label">Status</span>
            <span className="detail-value">
              {isEditing ? (
                <select className="inline-edit-input" value={formData.status} onChange={(e) => updateField('status', e.target.value)}>
                  <option value="Draft">Draft</option><option value="Sent">Sent</option><option value="Rejected">Rejected</option>
                  {quote.status === 'Accepted' && <option value="Accepted">Accepted</option>}
                </select>
              ) : (
                <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>
                  <span className={`badge badge-${getStatusColor(quote.status)}`}>{quote.status}</span>
                </span>
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Contact</span>
            <span className="detail-value">
              {isEditing ? (
                <div style={{ maxWidth: '350px' }}>
                  <TypeaheadSelect
                    id="quote-contact"
                    placeholder={quote.account_id ? "Search contacts..." : "Select an account first"}
                    items={accountContacts.map(c => ({ id: c.id, name: `${c.first_name} ${c.last_name || ''}`.trim() + (c.job_title ? ` - ${c.job_title}` : '') }))}
                    value={formData.contact_id}
                    displayValue={contactSearchText}
                    onChange={(cId, name) => { updateField('contact_id', cId); setContactSearchText(name); }}
                    disabled={!quote.account_id}
                    onAddNew={quote.account_id ? openNewContactModal : undefined}
                    addNewLabel="Create Contact"
                  />
                </div>
              ) : (
                <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>
                  {quote.contact_first_name ? `${quote.contact_first_name} ${quote.contact_last_name || ''}`.trim() : <EmptyValue />}
                </span>
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Sales Contact</span>
            <span className="detail-value">{quote.account_primary_owner ? quote.account_primary_owner.user_name : <EmptyValue label="Unassigned" />}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Owner</span>
            <span className="detail-value">
              {isEditing && !quote.opportunity_id ? (
                <select className="inline-edit-input" value={formData.owner_id} onChange={(e) => updateField('owner_id', e.target.value)}>
                  <option value="">Unassigned</option>
                  {users.filter(u => u.active).map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              ) : (
                <span className={canEdit && !quote.opportunity_id ? 'editable-value' : ''} onClick={() => { if (!quote.opportunity_id) startEditing(); }}>
                  {quote.owner_name || <EmptyValue label="Unassigned" />}
                </span>
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Rep Firm</span>
            <span className="detail-value">
              {isEditing && !quote.opportunity_id ? (
                <select className="inline-edit-input" value={formData.rep_firm_id} onChange={(e) => updateField('rep_firm_id', e.target.value)}>
                  <option value="">None</option>
                  {repFirms.map(rf => <option key={rf.id} value={rf.id}>{rf.name}</option>)}
                </select>
              ) : (
                <span className={canEdit && !quote.opportunity_id ? 'editable-value' : ''} onClick={() => { if (!quote.opportunity_id) startEditing(); }}>
                  {quote.rep_firm_name || <EmptyValue />}
                </span>
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Expiration Date</span>
            <span className="detail-value">
              {isEditing ? (
                <input type="date" className="inline-edit-input" value={formData.expiration_date} onChange={(e) => updateField('expiration_date', e.target.value)} />
              ) : (
                <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>
                  {quote.expiration_date ? new Date(quote.expiration_date).toLocaleDateString() : <EmptyValue />}
                </span>
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Est. Ship Date</span>
            <span className="detail-value">
              {isEditing ? (
                <input type="date" className="inline-edit-input" value={formData.estimated_ship_date} onChange={(e) => updateField('estimated_ship_date', e.target.value)} />
              ) : (
                <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>
                  {quote.estimated_ship_date ? new Date(quote.estimated_ship_date).toLocaleDateString() : <EmptyValue />}
                  {quote.allow_split_shipments === 1 && <span className="badge badge-gray" style={{ marginLeft: '8px' }}><Package size={12} /> Split OK</span>}
                </span>
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Total</span>
            <span className="detail-value" style={{ color: 'var(--success)', fontWeight: 600 }}>${fmtCurrency(quote.total)}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Created</span>
            <span className="detail-value">{new Date(quote.created_at).toLocaleString()} by {quote.created_by_name || 'Unknown'}</span>
          </div>
        </div>
      </div>

      {/* Addresses Card */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header"><h3 className="card-title">Addresses</h3></div>
        <div className="card-body">
          <div className="detail-row">
            <span className="detail-label">Bill-To</span>
            <span className="detail-value">
              {isEditing ? (
                <div style={{ maxWidth: '350px' }}>
                <TypeaheadSelect
                  id="quote-bill-to"
                  placeholder="Search addresses..."
                  items={addresses.map(a => ({ id: a.id, name: formatAddressName(a) }))}
                  value={formData.bill_to_address_id}
                  displayValue={billToSearchText}
                  onChange={(addrId, name) => { updateField('bill_to_address_id', addrId); setBillToSearchText(name); }}
                  onAddNew={quote.account_id ? () => openNewAddressModal('bill_to') : undefined}
                  addNewLabel="Add New Address"
                />
                </div>
              ) : (
                <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>
                  {quote.bill_to_line1 ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}><MapPin size={14} style={{ marginTop: '2px', flexShrink: 0 }} /><div><div>{quote.bill_to_line1}</div>{quote.bill_to_line2 && <div>{quote.bill_to_line2}</div>}<div>{quote.bill_to_city}{quote.bill_to_city && quote.bill_to_state ? ', ' : ''}{quote.bill_to_state} {quote.bill_to_zip}</div></div></div>
                  ) : <EmptyValue />}
                </span>
              )}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Ship-To</span>
            <span className="detail-value">
              {isEditing ? (
                <div style={{ maxWidth: '350px' }}>
                <TypeaheadSelect
                  id="quote-ship-to"
                  placeholder="Search addresses..."
                  items={addresses.map(a => ({ id: a.id, name: formatAddressName(a) }))}
                  value={formData.ship_to_address_id}
                  displayValue={shipToSearchText}
                  onChange={(addrId, name) => { updateField('ship_to_address_id', addrId); setShipToSearchText(name); }}
                  onAddNew={quote.account_id ? () => openNewAddressModal('ship_to') : undefined}
                  addNewLabel="Add New Address"
                />
                </div>
              ) : (
                <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>
                  {quote.ship_to_line1 ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px' }}><Truck size={14} style={{ marginTop: '2px', flexShrink: 0 }} /><div><div>{quote.ship_to_line1}</div>{quote.ship_to_line2 && <div>{quote.ship_to_line2}</div>}<div>{quote.ship_to_city}{quote.ship_to_city && quote.ship_to_state ? ', ' : ''}{quote.ship_to_state} {quote.ship_to_zip}</div></div></div>
                  ) : <EmptyValue />}
                </span>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* Terms & Notes Card */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div className="card-header"><h3 className="card-title">Terms & Notes</h3></div>
        <div className="card-body">
          <div className="detail-row">
            <span className="detail-label">Payment Terms</span>
            <span className="detail-value">
              {isEditing ? (
                <select className="inline-edit-input" value={formData.payment_terms} onChange={(e) => updateField('payment_terms', e.target.value)}>
                  <option value="">Select</option>
                  {['Net 15','Net 30','Net 45','Net 60','Due on Receipt','50% Advance','COD'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              ) : <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>{quote.payment_terms || <EmptyValue />}</span>}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Delivery Terms</span>
            <span className="detail-value">
              {isEditing ? (
                <select className="inline-edit-input" value={formData.delivery_terms} onChange={(e) => updateField('delivery_terms', e.target.value)}>
                  <option value="">Select</option>
                  {['FOB Origin','FOB Destination','CIF','EXW','DDP','FCA'].map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              ) : <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>{quote.delivery_terms || <EmptyValue />}</span>}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Currency</span>
            <span className="detail-value">
              {isEditing ? (
                <select className="inline-edit-input" value={formData.currency} onChange={(e) => updateField('currency', e.target.value)}>
                  <option value="">Default (USD)</option>
                  {['USD','EUR','GBP','CAD','AUD','JPY'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              ) : <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>{quote.currency || 'USD'}</span>}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Validity Period</span>
            <span className="detail-value">
              {isEditing ? <input type="text" className="inline-edit-input" value={formData.validity_period} onChange={(e) => updateField('validity_period', e.target.value)} placeholder="e.g. 30 days" />
              : <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>{quote.validity_period || <EmptyValue />}</span>}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Tax Rate</span>
            <span className="detail-value">
              {isEditing ? <input type="number" step="0.01" min="0" className="inline-edit-input" value={formData.tax_rate} onChange={(e) => updateField('tax_rate', e.target.value)} style={{ width: '100px' }} />
              : <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>{quote.tax_rate ? `${quote.tax_rate}%` : <EmptyValue />}</span>}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Shipping Cost</span>
            <span className="detail-value">
              {isEditing ? <input type="number" step="0.01" min="0" className="inline-edit-input" value={formData.shipping_cost} onChange={(e) => updateField('shipping_cost', e.target.value)} style={{ width: '120px' }} />
              : <span className={canEdit ? 'editable-value' : ''} onClick={startEditing}>{quote.shipping_cost ? `$${fmtCurrency(quote.shipping_cost)}` : <EmptyValue />}</span>}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Notes</span>
            <span className="detail-value">
              {isEditing ? <textarea className="inline-edit-input" value={formData.notes} rows={3} onChange={(e) => updateField('notes', e.target.value)} style={{ resize: 'vertical' }} />
              : <span className={canEdit ? 'editable-value' : ''} onClick={startEditing} style={{ whiteSpace: 'pre-wrap' }}>{quote.notes || <EmptyValue label="No notes" />}</span>}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Special Req.</span>
            <span className="detail-value">
              {isEditing ? <textarea className="inline-edit-input" value={formData.special_requirements} rows={3} onChange={(e) => updateField('special_requirements', e.target.value)} style={{ resize: 'vertical' }} />
              : <span className={canEdit ? 'editable-value' : ''} onClick={startEditing} style={{ whiteSpace: 'pre-wrap' }}>{quote.special_requirements || <EmptyValue label="Not set" />}</span>}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">Terms & Conditions</span>
            <span className="detail-value">
              {isEditing ? <textarea className="inline-edit-input" value={formData.terms_and_conditions} rows={4} onChange={(e) => updateField('terms_and_conditions', e.target.value)} style={{ resize: 'vertical' }} />
              : <span className={canEdit ? 'editable-value' : ''} onClick={startEditing} style={{ whiteSpace: 'pre-wrap' }}>{quote.terms_and_conditions || <EmptyValue label="Not set" />}</span>}
            </span>
          </div>
        </div>
      </div>

      {/* Line Items Card — read-only, at bottom */}
      {(() => {
        const taxRate = isEditing ? (parseFloat(formData.tax_rate) || 0) : (quote.tax_rate || 0);
        const shippingCost = isEditing ? (parseFloat(formData.shipping_cost) || 0) : (quote.shipping_cost || 0);
        const subtotal = quote.subtotal || 0;
        const taxAmount = subtotal * taxRate / 100;
        const grandTotal = subtotal + taxAmount + shippingCost;

        return (
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-header"><h3 className="card-title">Line Items</h3></div>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr><th scope="col">SKU</th><th scope="col">Product</th><th scope="col">Description</th><th scope="col">Qty</th><th scope="col">Unit Price</th><th scope="col">Discount</th><th scope="col">Lead Time</th><th scope="col">Total</th></tr>
                </thead>
                <tbody>
                  {quote.line_items?.length > 0 ? quote.line_items.map((item, i) => (
                    <tr key={i}>
                      <td>{item.sku || <EmptyValue label="No SKU" />}</td>
                      <td>{item.product_name}</td>
                      <td>{item.description || <EmptyValue label="No description" />}</td>
                      <td>{item.quantity}</td>
                      <td>${fmtCurrency(item.unit_price)}</td>
                      <td style={{ color: item.discount_value > 0 ? 'var(--accent-danger, #dc2626)' : undefined }}>{item.discount_type === 'percentage' && item.discount_value > 0 ? `${item.discount_value}%` : '-'}</td>
                      <td>{item.lead_time || <EmptyValue />}</td>
                      <td>${fmtCurrency(item.line_total)}</td>
                    </tr>
                  )) : <tr><td colSpan="8" className="text-center text-secondary">No line items</td></tr>}
                </tbody>
                {quote.line_items?.length > 0 && (
                  <tfoot>
                    <tr><td colSpan="7" style={{ textAlign: 'right', fontWeight: 600 }}>Subtotal</td><td style={{ fontWeight: 600 }}>${fmtCurrency(subtotal)}</td></tr>
                    {taxRate > 0 && <tr><td colSpan="7" style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>Tax ({taxRate}%)</td><td>${fmtCurrency(taxAmount)}</td></tr>}
                    {shippingCost > 0 && <tr><td colSpan="7" style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>Shipping</td><td>${fmtCurrency(shippingCost)}</td></tr>}
                    <tr style={{ borderTop: '2px solid var(--border-color)' }}><td colSpan="7" style={{ textAlign: 'right', fontWeight: 700, fontSize: '15px' }}>Grand Total</td><td style={{ fontWeight: 700, fontSize: '15px' }}>${fmtCurrency(grandTotal)}</td></tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        );
      })()}

      <ConfirmDialog isOpen={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onConfirm={handleDelete} title="Delete Quote" message="Delete this quote? This cannot be undone." confirmText="Delete" />

      <Modal isOpen={showNewAddressModal} onClose={() => setShowNewAddressModal(false)} title="Create New Address">
        <form onSubmit={handleCreateAddress}>
          <div className="modal-body">
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Address Type</label>
                <select className="form-select" value={newAddressForm.address_type} onChange={(e) => setNewAddressForm({ ...newAddressForm, address_type: e.target.value })}>
                  <option value="Headquarters">Headquarters</option><option value="Shipping">Shipping</option><option value="Billing">Billing</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">&nbsp;</label>
                <div className="checkbox-group">
                  <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={newAddressForm.is_headquarters} onChange={(e) => setNewAddressForm({ ...newAddressForm, is_headquarters: e.target.checked })} /> Is Headquarters</label>
                  <label className="checkbox-wrapper"><input type="checkbox" className="checkbox" checked={newAddressForm.is_default_shipping} onChange={(e) => setNewAddressForm({ ...newAddressForm, is_default_shipping: e.target.checked })} /> Default Shipping</label>
                </div>
              </div>
            </div>
            <div className="form-group"><label className="form-label">Address Line 1</label><input type="text" className="form-input" value={newAddressForm.address_line1} onChange={(e) => setNewAddressForm({ ...newAddressForm, address_line1: e.target.value })} required /></div>
            <div className="form-group"><label className="form-label">Address Line 2</label><input type="text" className="form-input" value={newAddressForm.address_line2} onChange={(e) => setNewAddressForm({ ...newAddressForm, address_line2: e.target.value })} /></div>
            <div className="grid grid-cols-2">
              <div className="form-group"><label className="form-label">City</label><input type="text" className="form-input" value={newAddressForm.city} onChange={(e) => setNewAddressForm({ ...newAddressForm, city: e.target.value })} required /></div>
              <div className="form-group"><label className="form-label">State/Province</label><input type="text" className="form-input" value={newAddressForm.state} onChange={(e) => setNewAddressForm({ ...newAddressForm, state: e.target.value })} required /></div>
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group"><label className="form-label">ZIP/Postal Code</label><input type="text" className="form-input" value={newAddressForm.zip_code} onChange={(e) => setNewAddressForm({ ...newAddressForm, zip_code: e.target.value })} /></div>
              <div className="form-group"><label className="form-label">Country</label><input type="text" className="form-input" value={newAddressForm.country} onChange={(e) => setNewAddressForm({ ...newAddressForm, country: e.target.value })} /></div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setShowNewAddressModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Create Address</button>
          </div>
        </form>
      </Modal>

      {/* Create Contact Modal */}
      <Modal isOpen={showNewContactModal} onClose={() => setShowNewContactModal(false)} title="Create Contact">
        <form onSubmit={handleCreateContact}>
          <div className="modal-body">
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">First Name *</label>
                <input type="text" className="form-input" value={newContactForm.first_name} onChange={(e) => setNewContactForm({ ...newContactForm, first_name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input type="text" className="form-input" value={newContactForm.last_name} onChange={(e) => setNewContactForm({ ...newContactForm, last_name: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2">
              <div className="form-group">
                <label className="form-label">Email</label>
                <input type="email" className="form-input" value={newContactForm.email} onChange={(e) => setNewContactForm({ ...newContactForm, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Phone</label>
                <input type="tel" className="form-input" value={newContactForm.phone} onChange={(e) => setNewContactForm({ ...newContactForm, phone: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Job Title</label>
              <input type="text" className="form-input" value={newContactForm.job_title} onChange={(e) => setNewContactForm({ ...newContactForm, job_title: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setShowNewContactModal(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary">Create Contact</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default QuoteDetail;
